<template>
  <div class="p-4 space-y-4">
    <!-- Encabezado -->
    <div class="flex flex-col gap-3">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-xl font-bold text-gray-900 dark:text-gray-100">Tablero Financiero</h1>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Resumen general de tus finanzas</p>
        </div>
        <button
          @click="recargarDatos"
          :disabled="cargando"
          class="px-3 py-1.5 text-sm font-medium rounded-md bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
        >
          <svg class="w-4 h-4" :class="{ 'animate-spin': cargando }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Actualizar
        </button>
      </div>

      <!-- Filtros de Fecha -->
      <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-3">
        <div class="flex items-center gap-3">
          <div class="flex items-center gap-2">
            <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Desde:</label>
            <input
              v-model="filtros.fecha_inicio"
              type="date"
              @change="aplicarFiltros"
              class="px-2 py-1 text-xs border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div class="flex items-center gap-2">
            <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Hasta:</label>
            <input
              v-model="filtros.fecha_fin"
              type="date"
              @change="aplicarFiltros"
              class="px-2 py-1 text-xs border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div class="flex gap-1">
            <button @click="establecerPeriodo('hoy')" class="px-2 py-1 text-xs font-medium rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">Hoy</button>
            <button @click="establecerPeriodo('semana')" class="px-2 py-1 text-xs font-medium rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">Semana</button>
            <button @click="establecerPeriodo('mes')" class="px-2 py-1 text-xs font-medium rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">Mes</button>
            <button @click="establecerPeriodo('trimestre')" class="px-2 py-1 text-xs font-medium rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">Trimestre</button>
            <button @click="establecerPeriodo('año')" class="px-2 py-1 text-xs font-medium rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">Año</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Estado de carga -->
    <div v-if="cargando && !resumen" class="text-center py-12">
      <div class="inline-block w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Cargando datos...</p>
    </div>

    <!-- Contenido principal -->
    <div v-else-if="resumen">
      <!-- Tarjetas de estadísticas principales -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <TarjetaEstadistica
          etiqueta="Saldo Total"
          :monto="resumen.saldo_total"
          color="blue"
        />
        <TarjetaEstadistica
          etiqueta="Ingresos del Periodo"
          :monto="resumen.ingresos_periodo"
          color="green"
        />
        <TarjetaEstadistica
          etiqueta="Gastos del Periodo"
          :monto="resumen.gastos_periodo"
          color="red"
        />
        <TarjetaEstadistica
          etiqueta="Balance del Periodo"
          :monto="resumen.balance_periodo"
          :color="resumen.balance_periodo >= 0 ? 'green' : 'red'"
        />
      </div>

      <!-- KPIs adicionales -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-3">
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Transacciones Ingresos</p>
          <p class="text-2xl font-bold text-green-600 dark:text-green-400">{{ resumen.cantidad_ingresos }}</p>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Promedio: <EtiquetaMonto :monto="resumen.promedio_ingresos" size="xs" /></p>
        </div>
        <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-3">
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Transacciones Gastos</p>
          <p class="text-2xl font-bold text-red-600 dark:text-red-400">{{ resumen.cantidad_gastos }}</p>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Promedio: <EtiquetaMonto :monto="resumen.promedio_gastos" size="xs" /></p>
        </div>
        <div v-if="resumen.categoria_top_ingreso" class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-3">
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Top Categoría Ingreso</p>
          <p class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{{ resumen.categoria_top_ingreso.categoria }}</p>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1"><EtiquetaMonto :monto="resumen.categoria_top_ingreso.total" size="xs" /></p>
        </div>
        <div v-if="resumen.categoria_top_gasto" class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-3">
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Top Categoría Gasto</p>
          <p class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{{ resumen.categoria_top_gasto.categoria }}</p>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1"><EtiquetaMonto :monto="resumen.categoria_top_gasto.total" size="xs" /></p>
        </div>
      </div>

      <!-- Cuentas por Cobrar y Pagar -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <TarjetaEstadistica
          etiqueta="Cuentas por Cobrar"
          :monto="resumen.cuentas_por_cobrar"
          color="yellow"
          subtitulo="Pendientes de cobro"
        />
        <TarjetaEstadistica
          etiqueta="Cuentas por Pagar"
          :monto="resumen.cuentas_por_pagar"
          color="purple"
          subtitulo="Pendientes de pago"
        />
      </div>

      <!-- Gráficas -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <!-- Gráfica de Ingresos vs Gastos -->
        <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-4">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Ingresos vs Gastos (Últimos 6 meses)</h3>
          <div v-if="resumen.ingresos_vs_gastos && resumen.ingresos_vs_gastos.length > 0" class="h-64">
            <BarChart
              :datos="resumen.ingresos_vs_gastos"
              campo-etiqueta="mes"
              :campos-valor="['ingresos', 'gastos']"
              :colores="['#10B981', '#EF4444']"
              :etiquetas="['Ingresos', 'Gastos']"
            />
          </div>
          <div v-else class="h-64 flex items-center justify-center text-gray-400">
            <p class="text-sm">No hay datos disponibles</p>
          </div>
        </div>

        <!-- Gráfica de Gastos por Categoría -->
        <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-4">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Gastos por Categoría (Este mes)</h3>
          <div v-if="resumen.gastos_por_categoria && resumen.gastos_por_categoria.length > 0" class="h-64">
            <BarChart
              :datos="resumen.gastos_por_categoria"
              campo-etiqueta="categoria"
              campo-valor="total"
              color="#8B5CF6"
            />
          </div>
          <div v-else class="h-64 flex items-center justify-center text-gray-400">
            <p class="text-sm">No hay datos disponibles</p>
          </div>
        </div>
      </div>

      <!-- Cuentas Bancarias -->
      <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-4 mt-4">
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">Cuentas Bancarias</h3>
          <router-link
            to="/finanzas/cuentas-bancarias"
            class="text-xs text-violet-600 hover:text-violet-700 font-medium"
          >
            Ver todas →
          </router-link>
        </div>
        <div v-if="resumen.cuentas_bancarias && resumen.cuentas_bancarias.length > 0" class="space-y-2">
          <div
            v-for="cuenta in resumen.cuentas_bancarias"
            :key="cuenta.id"
            class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
          >
            <div class="flex-1">
              <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ cuenta.nombre }}</p>
              <p class="text-xs text-gray-500 dark:text-gray-400">{{ cuenta.banco }} - {{ cuenta.numero_cuenta }}</p>
            </div>
            <EtiquetaMonto :monto="cuenta.saldo_actual" size="base" bold />
          </div>
        </div>
        <div v-else class="text-center py-6 text-gray-400">
          <p class="text-sm">No hay cuentas bancarias registradas</p>
        </div>
      </div>
    </div>

    <!-- Estado de error -->
    <div v-else-if="error" class="text-center py-12">
      <div class="text-red-500 mb-2">
        <svg class="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <p class="text-sm text-gray-500 dark:text-gray-400">{{ error }}</p>
      <button
        @click="recargarDatos"
        class="mt-3 px-4 py-2 text-sm font-medium rounded-md bg-violet-600 text-white hover:bg-violet-700"
      >
        Reintentar
      </button>
    </div>
  </div>
</template>

<script setup>
import { onMounted } from 'vue'
import { useTableroFinanciero } from '@/composables/finanzas/useTableroFinanciero'
import TarjetaEstadistica from '@/components/Compartidos/TarjetaEstadistica.vue'
import EtiquetaMonto from '@/components/Compartidos/EtiquetaMonto.vue'
import BarChart from '@/components/charts/BarChart.vue'

const { resumen, cargando, error, filtros, cargarResumen, recargarTodo } = useTableroFinanciero()

const recargarDatos = async () => {
  await recargarTodo()
}

const aplicarFiltros = async () => {
  await cargarResumen({
    fecha_inicio: filtros.value.fecha_inicio,
    fecha_fin: filtros.value.fecha_fin
  })
}

const establecerPeriodo = async (periodo) => {
  const hoy = new Date()
  let inicio, fin

  switch (periodo) {
    case 'hoy':
      inicio = fin = hoy.toISOString().split('T')[0]
      break
    case 'semana':
      const primerDiaSemana = new Date(hoy)
      primerDiaSemana.setDate(hoy.getDate() - hoy.getDay())
      inicio = primerDiaSemana.toISOString().split('T')[0]
      fin = hoy.toISOString().split('T')[0]
      break
    case 'mes':
      inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString().split('T')[0]
      fin = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).toISOString().split('T')[0]
      break
    case 'trimestre':
      const mesInicio = Math.floor(hoy.getMonth() / 3) * 3
      inicio = new Date(hoy.getFullYear(), mesInicio, 1).toISOString().split('T')[0]
      fin = new Date(hoy.getFullYear(), mesInicio + 3, 0).toISOString().split('T')[0]
      break
    case 'año':
      inicio = new Date(hoy.getFullYear(), 0, 1).toISOString().split('T')[0]
      fin = new Date(hoy.getFullYear(), 11, 31).toISOString().split('T')[0]
      break
  }

  filtros.value.fecha_inicio = inicio
  filtros.value.fecha_fin = fin
  await aplicarFiltros()
}

onMounted(async () => {
  await cargarResumen()
})
</script>
